import styled from "styled-components";
import { useString as s } from "../../../../components/StringProvider";
import Heading from "../../../../components/Heading";
import Currency from "../../../../components/Currency";
import PropTypes from "prop-types";
import Tooltip from "../../../../components/Tooltip";
import { useContext } from "react";
import {
  CostOfStatusQuoChartContext,
  CostOfStatusQuoChartModes
} from "../CostOfStatusQuoChartProvider";
import classNames from "classnames";

const TotalColumn = ({
  numberOfCurrencyKPIs,
  numberOfNonCurrencyKPIs,
  totalAmount,
  currency
}) => {
  const { chartMode } = useContext(CostOfStatusQuoChartContext);
  const totalText = s("discovery.opportunity.page.cosqTotal", "Total");
  const tbdText = s("discovery.opportunity.page.tbd", "Additional Benefits");
  const gridColumnStart = numberOfCurrencyKPIs + numberOfNonCurrencyKPIs + 1;

  return (
    <>
      <TitleContainer
        numberOfCurrencyKPIs={numberOfCurrencyKPIs}
        numberOfNonCurrencyKPIs={numberOfNonCurrencyKPIs}
        className={"title"}
        gridColumnStart={gridColumnStart}
      >
        <Heading level={"h5"} color={"gray4"}>
          {totalText}
        </Heading>
      </TitleContainer>
      <CurrencyContainer
        numberOfCurrencyKPIs={numberOfCurrencyKPIs}
        numberOfNonCurrencyKPIs={numberOfNonCurrencyKPIs}
        className={classNames({
          currency: true,
          amount: true,
          benefitSummary:
            chartMode === CostOfStatusQuoChartModes.BENEFIT_SUMMARY
        })}
        gridColumnStart={gridColumnStart}
        gridRowStart={
          chartMode === CostOfStatusQuoChartModes.COST_OF_STATUS_QUO
            ? 2
            : numberOfNonCurrencyKPIs + 2
        }
        gridRowEnd={numberOfCurrencyKPIs}
      >
        <Heading level={"h4"}>
          <Currency format="short" currency={currency}>
            {totalAmount}
          </Currency>
        </Heading>
      </CurrencyContainer>
      {!!numberOfNonCurrencyKPIs && (
        <AdditionalBenefitsContainer
          numberOfCurrencyKPIs={numberOfCurrencyKPIs}
          numberOfNonCurrencyKPIs={numberOfNonCurrencyKPIs}
          className={classNames({
            tbd: true,
            benefitSummary:
              chartMode === CostOfStatusQuoChartModes.BENEFIT_SUMMARY
          })}
          data-cy={"cosq-additional-benefits-section"}
          gridColumnStart={gridColumnStart}
          gridRowStart={
            chartMode === CostOfStatusQuoChartModes.COST_OF_STATUS_QUO
              ? numberOfCurrencyKPIs + 2
              : 2
          }
          gridRowEnd={numberOfNonCurrencyKPIs}
        >
          {numberOfNonCurrencyKPIs === 1 && (
            <Tooltip title={tbdText} placement={"top"}>
              <Heading level={"h5"} useEllipsis={true}>
                {tbdText}
              </Heading>
            </Tooltip>
          )}
          {numberOfNonCurrencyKPIs > 1 && (
            <Heading level={"h5"}>{tbdText}</Heading>
          )}
        </AdditionalBenefitsContainer>
      )}
    </>
  );
};

const TitleContainer = styled.div`
  margin-left: 24px;
  grid-row-start: 1;
  grid-column-start: ${({ gridColumnStart }) => gridColumnStart};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  & h5 {
    text-align: center;
  }
`;

const CurrencyContainer = styled.div`
  margin-left: 24px;
  grid-row-start: ${({ gridRowStart }) => gridRowStart};
  grid-row-end: ${({ gridRowEnd }) => gridRowEnd} span;
  grid-column-start: ${({ gridColumnStart }) => gridColumnStart};

  border-radius: 4px;
  border: 1px dotted transparent;
  background: #e33d1f;

  &.benefitSummary {
    background: #15ac31;
    border: 1px solid #15ac31;
  }

  display: flex;
  flex-direction: column;
  justify-content: center;

  & h4 {
    color: white;
    text-align: center;
  }
`;

const AdditionalBenefitsContainer = styled.div`
  margin-left: 24px;
  grid-row-start: ${({ gridRowStart }) => gridRowStart};
  grid-row-end: ${({ gridRowEnd }) => gridRowEnd} span;
  grid-column-start: ${({ gridColumnStart }) => gridColumnStart};
  background-color: #ff9e8d;
  border-radius: 4px;
  border: 1px dashed #e33d1f;
  text-align: center;
  vertical-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &.benefitSummary {
    background: #82cb90;
    border: 1px solid #82cb90;
  }

  & h5 {
    color: white;
    text-align: center;
  }
`;

TotalColumn.propTypes = {
  currency: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
  numberOfCurrencyKPIs: PropTypes.number.isRequired,
  numberOfNonCurrencyKPIs: PropTypes.number.isRequired
};

export default TotalColumn;
